<script setup>
/**
 * MobileStickyCTA — bottom-fixed CTA bar visible only on mobile.
 *
 * Appears after the user has scrolled past the hero (so it doesn't compete
 * with the inline form), and stays accessible through the entire page.
 * Hidden on lg+ where the page header CTA is always reachable.
 *
 * Pattern: standard SaaS mobile UX — every competitor with serious mobile
 * traffic does this (Hubstaff, TimeCamp, ClickUp).
 */
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight, X } from "lucide-vue-next";

const { t } = useI18n();

const visible = ref(false);
const dismissed = ref(false);

const onScroll = () => {
  if (dismissed.value) return;
  // Show after one viewport of scroll
  visible.value = window.scrollY > window.innerHeight * 0.8;
};

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();
});
onUnmounted(() => window.removeEventListener("scroll", onScroll));

const dismiss = () => {
  dismissed.value = true;
  visible.value = false;
};
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="visible"
      class="fixed bottom-0 inset-x-0 z-40 lg:hidden p-3 bg-card border-t border-border shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur-md"
    >
      <div class="flex items-center gap-2 max-w-xl mx-auto">
        <div class="flex-1 min-w-0">
          <p class="text-xs font-semibold text-foreground leading-tight">
            {{ t("landing.mobile_sticky.title") }}
          </p>
          <p class="text-[10px] text-muted-foreground leading-tight truncate">
            {{ t("landing.mobile_sticky.subtitle") }}
          </p>
        </div>
        <a href="#contact-form" class="flex-shrink-0">
          <Button variant="hero-primary" size="default">
            {{ t("landing.mobile_sticky.cta") }}
            <ArrowRight class="w-3.5 h-3.5" />
          </Button>
        </a>
        <button
          type="button"
          @click="dismiss"
          :aria-label="t('landing.mobile_sticky.dismiss')"
          class="flex-shrink-0 p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
